/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";

export const useInvoiceCalculations = (invoice) => {
  const calculations = useMemo(() => {
    if (!invoice) {
      return {
        hasDiscount: false,
        hasTip: false,
        hasTaxes: false,
        hasDeposit: false,
        hasRestAmountWithDeposit: false,
        hasDepositOnly: false,
        price: 0,
        deposit: 0,
        discount: 0,
        tip: null,
        totalToPay: 0,
        payableAmount: 0,
        restAmount: 0,
        taxes: [],
        currency: null,
        isDepositPayment: false,
      };
    }

    const hasDiscount = invoice.discount_amount != null && Number(invoice.discount_amount) > 0;
    const hasTip = invoice.tip != null && invoice.tip.amount > 0;
    const hasTaxes = invoice.taxes?.length > 0;
    const hasSelectedPackages = invoice.package_instances?.length > 0;
    const hasDeposit = invoice.is_with_deposit_amount;
    const hasRestAmountWithDeposit = hasDeposit && invoice.rest_amount > 0;
    const hasDepositOnly = hasDeposit && invoice.rest_amount === 0;

    const price = hasRestAmountWithDeposit ? invoice.rest_amount : invoice.amount;
    const deposit = hasDeposit ? (hasRestAmountWithDeposit ? invoice.amount : invoice.deposit) : null;
    const discount = Number(invoice.discount_amount) > 0 ? invoice.discount_amount : null;
    const tip = hasTip ? invoice.tip : null;

    const totalToPay = invoice.amount + (invoice.rest_amount || 0);
    const payableAmount = invoice.amount;
    const restAmount = invoice.rest_amount || 0;

    const taxes = hasTaxes ? invoice.taxes.filter((tax) => tax.amount > 0) : [];

    const isDepositPayment = hasDeposit && !hasRestAmountWithDeposit && deposit > 0 && !hasSelectedPackages;

    return {
      hasDiscount,
      hasTip,
      hasTaxes,
      hasDeposit,
      hasRestAmountWithDeposit,
      hasDepositOnly,
      price,
      deposit,
      discount,
      tip,
      payableAmount,
      totalToPay,
      restAmount,
      taxes,
      currency: invoice.currency,
      isDepositPayment,
    };
  }, [invoice]);

  return calculations;
};
